import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import {
  EDashboardBookingStatus,
  ESalesMetricsGroupBy,
  ISalesMetricsFilters,
  ISalesMetricsFiltersResponse,
} from 'services/DashboardApi/types';
import { IMultiSelectWithSearchOption } from 'ui/MultiSelectWithSearch/MultiSelectOption';
import { MultiSelectWithSearch } from 'ui/MultiSelectWithSearch/MultiSelectWithSearch';
import { theme } from '../../../../../tailwind.config';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import FluidButton from 'ui/FluidButton';
import { ESalesMetricsFiltersAction, ISalesMetricsFiltersAction } from './hooks/useSalesMetricsFiltersReducer';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';

const statusColorMapping = {
  [EDashboardBookingStatus.BASKET]: null,
  [EDashboardBookingStatus.ENQUIRY]: theme.colors['status-enquiry'],
  [EDashboardBookingStatus.REQUESTED]: theme.colors['status-requested'],
  [EDashboardBookingStatus.CONFIRMED]: theme.colors['status-confirmed'],
  [EDashboardBookingStatus.COMPLETED]: theme.colors['status-completed'],
  [EDashboardBookingStatus.CANCELLED]: theme.colors['status-cancelled'],
  [EDashboardBookingStatus.DISCARDED]: theme.colors['status-discarded'],
};

export enum ESalesMetricsGroupByLabel {
  CREATION_DATE = 'Creation Date',
  ARRIVAL_DATE = 'Arrival Date',
}

export const groupByOptions: ISingleSelectOption[] = [
  {
    label: ESalesMetricsGroupByLabel.CREATION_DATE,
    value: ESalesMetricsGroupBy.CREATION_DATE,
  },
  {
    label: ESalesMetricsGroupByLabel.ARRIVAL_DATE,
    value: ESalesMetricsGroupBy.ARRIVAL_DATE,
  },
];

interface ISalesMetricsProps {
  filterOptions: ISalesMetricsFiltersResponse;
  filters: ISalesMetricsFilters;
  dispatchFilters: (action: ISalesMetricsFiltersAction) => void;
  onApplyFilters?: () => void;
}

export const SalesMetricsFilters: React.FC<ISalesMetricsProps> = ({
  filterOptions,
  filters,
  dispatchFilters,
  onApplyFilters,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const isTabletLayout = !isMobile && isTablet;
  const isDefaultLayout = !isMobile && !isTabletLayout;

  const { travelCompanyCountries, travelCompanies, destinations, hotels, statuses } = filterOptions;

  const handleGroupByChange = useCallback((value: ESalesMetricsGroupBy) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_DATE_TYPE, payload: value });
  }, []);

  const statusOptions: IMultiSelectWithSearchOption[] = useMemo(
    () =>
      statuses.map(status => ({
        label: status.name,
        value: status.id,
        renderItem: (isSelected: boolean, className?: string) => (
          <div className={`flex items-center justify-between w-full ${className || ''}`}>
            <span className="font-pt-sans text-16px leading-[normal]">{status.name}</span>
            <div
              className="w-[12px] h-[12px] rounded-full mr-[10px]"
              style={{ backgroundColor: statusColorMapping[status.id] }}
            />
          </div>
        ),
      })),
    [statuses]
  );

  const handleStatusesChange = useCallback((values: EDashboardBookingStatus[]) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_STATUS, payload: values });
  }, []);

  const travelCompanyCountriesOptions: IMultiSelectWithSearchOption[] = useMemo(
    () =>
      travelCompanyCountries.map(country => ({
        label: country.name,
        value: country.id,
      })),
    [travelCompanyCountries]
  );

  const travelCompaniesOptions: IMultiSelectWithSearchOption[] = useMemo(() => {
    if (filters.companyCountry.length === 0) {
      return travelCompanies.map(company => ({
        label: company.name,
        value: company.id,
      }));
    }
    const filteredCompanies = travelCompanies.filter(company => filters.companyCountry.includes(company.countryId));
    return filteredCompanies.map(company => ({
      label: company.name,
      value: company.id,
    }));
  }, [travelCompanies, filters.companyCountry]);

  const destinationsOptions: IMultiSelectWithSearchOption[] = useMemo(
    () =>
      destinations.map(destination => ({
        label: destination.name,
        value: destination.id,
      })),
    [destinations]
  );

  const hotelsOptions: IMultiSelectWithSearchOption[] = useMemo(() => {
    if (filters.destination.length === 0) {
      return hotels.map(hotel => ({
        label: hotel.name,
        value: hotel.id,
      }));
    }
    const filteredHotels = hotels.filter(hotel => filters.destination.includes(hotel.destinationId));
    return filteredHotels.map(hotel => ({
      label: hotel.name,
      value: hotel.id,
    }));
  }, [hotels, filters.destination]);

  const handleTravelCompanyCountriesChange = useCallback((values: string[]) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_COMPANY_COUNTRY, payload: values });
  }, []);

  const handleTravelCompaniesChange = useCallback((values: string[]) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_COMPANY, payload: values });
  }, []);

  const handleDestinationsChange = useCallback((values: string[]) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_DESTINATION, payload: values });
  }, []);

  const handleHotelsChange = useCallback((values: string[]) => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.SET_HOTEL, payload: values });
  }, []);

  const handleResetFilters = useCallback(() => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.RESET_FILTERS });
  }, []);

  const handleMobileResetFilters = useCallback(() => {
    dispatchFilters({ type: ESalesMetricsFiltersAction.RESET_FILTERS });
    onApplyFilters?.();
  }, [onApplyFilters]);

  const bookingStatusFilter = (
    <MultiSelectWithSearch
      fieldId="booking-status"
      label="Current Booking Status"
      options={statusOptions}
      value={filters.status}
      onChange={handleStatusesChange}
      showSelectAllOption
      className={classNames('min-w-0', {
        'w-[180px]': isDefaultLayout,
        'flex-1': isTabletLayout,
        'w-full': isMobile,
      })}
      applyChangesOnClose
      maxVisibleItems={12}
    />
  );

  const travelCompanyCountriesFilter = (
    <MultiSelectWithSearch
      fieldId="travel-company-countries"
      label="Travel Company Countries"
      options={travelCompanyCountriesOptions}
      value={filters.companyCountry}
      onChange={handleTravelCompanyCountriesChange}
      maxVisibleItems={8}
      allowFiltering
      showSelectAllOption
      allowGrouping
      allowSorting
      customLabelWhenNothingSelected="All"
      className={classNames('min-w-0', {
        'min-w-[200px] flex-1': isDefaultLayout,
        'flex-1': isTabletLayout,
        'w-full': isMobile,
      })}
      applyChangesOnClose
    />
  );

  const travelCompaniesFilter = (
    <MultiSelectWithSearch
      fieldId="travel-companies"
      label="Travel Companies"
      options={travelCompaniesOptions}
      value={filters.company}
      onChange={handleTravelCompaniesChange}
      maxVisibleItems={8}
      allowFiltering
      showSelectAllOption
      allowGrouping
      allowSorting
      customLabelWhenNothingSelected="All"
      className={classNames('min-w-0 flex-1', {
        'min-w-[200px]': isDefaultLayout,
        'w-full': isMobile,
      })}
      applyChangesOnClose
    />
  );

  const destinationsFilter = (
    <MultiSelectWithSearch
      fieldId="destinations"
      label="Destinations"
      options={destinationsOptions}
      value={filters.destination}
      onChange={handleDestinationsChange}
      maxVisibleItems={8}
      showSelectAllOption
      allowGrouping
      allowSorting
      customLabelWhenNothingSelected="All"
      className={classNames('min-w-0 flex-1', {
        'min-w-[200px]': isDefaultLayout,
        'w-full': isMobile,
      })}
      applyChangesOnClose
    />
  );

  const hotelsFilter = (
    <MultiSelectWithSearch
      fieldId="hotels"
      label="Hotels"
      options={hotelsOptions}
      value={filters.hotel}
      onChange={handleHotelsChange}
      maxVisibleItems={8}
      allowFiltering
      showSelectAllOption
      allowGrouping
      allowSorting
      customLabelWhenNothingSelected="All"
      className={classNames('min-w-0 flex-1', {
        'min-w-[200px]': isDefaultLayout,
        'w-full': isMobile,
      })}
      applyChangesOnClose
    />
  );

  const groupByFilter = (
    <SingleSelect
      fieldId="group-by"
      label="Group By"
      value={filters.dateType}
      options={groupByOptions}
      onChange={handleGroupByChange}
      className={classNames('group-by-status min-w-0 flex-1', {
        'min-w-[150px] max-w-[150px]': isDefaultLayout || isTabletLayout,
        'w-full': isMobile,
      })}
    />
  );

  if (isMobile) {
    return (
      <div className="sales-metrics-filters flex flex-col justify-between h-full">
        <div className="sales-metrics-filters flex flex-col gap-[15px]">
          {groupByFilter}
          {bookingStatusFilter}
          {travelCompanyCountriesFilter}
          {travelCompaniesFilter}
          {destinationsFilter}
          {hotelsFilter}
        </div>

        <div className="buttons pt-[15px] flex gap-[10px]">
          <FluidButton type="secondary" onClick={handleMobileResetFilters} className="flex-1">
            Reset Filters
          </FluidButton>
          <FluidButton type="primary" onClick={onApplyFilters} className="flex-1">
            Apply Filters
          </FluidButton>
        </div>
      </div>
    );
  }

  if (isTabletLayout) {
    return (
      <div className="sales-metrics-filters">
        <div className="sales-metrics-filters flex flex-col gap-[15px]">
          <div className="flex gap-[20px] items-end">
            {groupByFilter}
            {bookingStatusFilter}
            {travelCompanyCountriesFilter}
          </div>
          <div className="flex gap-[20px] items-end">
            {travelCompaniesFilter}
            {destinationsFilter}
            {hotelsFilter}
          </div>
        </div>

        <div className="buttons pt-[15px]">
          <FluidButton type="secondary" onClick={handleResetFilters}>
            Reset Filters
          </FluidButton>
        </div>
      </div>
    );
  }

  return (
    <div className="sales-metrics-filters">
      <div className="sales-metrics-filters flex items-center gap-[20px]">
        {groupByFilter}
        {bookingStatusFilter}
        {travelCompanyCountriesFilter}
        {travelCompaniesFilter}
        {destinationsFilter}
        {hotelsFilter}
      </div>

      <div className="buttons pt-[15px]">
        <FluidButton type="secondary" onClick={handleResetFilters}>
          Reset Filters
        </FluidButton>
      </div>
    </div>
  );
};
